import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PREP_COL,
  PREP_TABLE_MIN_WIDTH,
  prepTdNumSx,
  prepTdSx,
  prepThSx,
  shellSx,
} from '../constants';
import {
  getInvestorGuidanceText,
  getLocalizedBreakoutLabel,
  regimeColor,
  signalColor,
  triggerColor,
} from '../utils/labels';
import {
  PrepCandidatesRowSkeleton,
  PrepCandidatesStatsSkeleton,
} from './PrepSkeletons';

export default function PreparationCandidatesTable({
  t,
  i18n,
  tableExportRef,
  loading,
  candidates,
  stats,
  topScore,
  avgScore,
  fmtNum,
  fmtPct,
  fmtMultiplier,
  getSignalLabel,
  getRegimeLabel,
  getTriggerLabel,
  getSectorLabel,
  getSignalHint,
  localizeReason,
  onOpenDetail,
}) {
  return (
        <Box sx={{ ...shellSx }} ref={tableExportRef}>
          <Box sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            px: 1.5,
            py: 0.85,
            borderBottom: '1px solid #eef2f7',
            bgcolor: '#fafbfc',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', minWidth: 0 }}>
              {loading && candidates.length === 0 ? (
                <PrepCandidatesStatsSkeleton />
              ) : (
                <>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a' }}>
                <Box component="span" sx={{ fontSize: '0.95rem', fontWeight: 900, mr: 0.4 }}>{candidates.length}</Box>
                {t('preparation.stats.total')}
              </Typography>
              {candidates.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', height: 5, width: 120, borderRadius: 99, overflow: 'hidden', bgcolor: '#e2e8f0' }}>
                      <Box sx={{ width: `${(stats.prepBuy / candidates.length) * 100}%`, bgcolor: '#16a34a' }} />
                      <Box sx={{ width: `${(stats.cautionBuy / candidates.length) * 100}%`, bgcolor: '#b45309' }} />
                      <Box sx={{ width: `${(stats.watch / candidates.length) * 100}%`, bgcolor: '#2563eb' }} />
                      <Box sx={{ flex: 1, bgcolor: '#cbd5e1' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <Box component="span" sx={{ color: '#16a34a' }}>{stats.prepBuy} {getSignalLabel('BUY_PREP')}</Box>
                      <Box component="span" sx={{ color: '#94a3b8' }}> · </Box>
                      <Box component="span" sx={{ color: '#b45309' }}>{stats.cautionBuy} {getSignalLabel('BUY_CAUTION')}</Box>
                      <Box component="span" sx={{ color: '#94a3b8' }}> · </Box>
                      <Box component="span" sx={{ color: '#2563eb' }}>{stats.watch} {getSignalLabel('BUY_WATCH')}</Box>
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {t('preparation.history.metrics.triggered')}: {stats.triggered}
                    {' · '}
                    {t('preparation.stats.topScore')}: {topScore != null ? fmtNum(topScore, 1) : '—'}
                    {avgScore != null ? ` (${t('preparation.stats.topScoreHint', { value: fmtNum(avgScore, 1) })})` : ''}
                  </Typography>
                  {stats.dominant && (
                    <Chip
                      size="small"
                      label={getRegimeLabel(stats.dominant)}
                      sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: `${regimeColor(stats.dominant)}18`, color: regimeColor(stats.dominant) }}
                    />
                  )}
                </>
              )}
                </>
              )}
            </Box>
          </Box>

          <TableContainer sx={{ p: 0 }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: PREP_TABLE_MIN_WIDTH,
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { boxSizing: 'border-box' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.ticker }}>{t('preparation.table.headers.ticker')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.score, textAlign: 'right' }}>{t('preparation.table.headers.score')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.signal }}>{t('preparation.table.headers.signal')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.sector }}>{t('preparation.table.headers.sector')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.close, textAlign: 'right' }}>{t('preparation.table.headers.close')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.dist, textAlign: 'right' }}>{t('preparation.table.headers.distResistance')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.accum, textAlign: 'right' }}>{t('preparation.table.headers.accumulation')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.upDown, textAlign: 'right' }}>{t('preparation.table.headers.upDown')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.trigger }}>{t('preparation.table.headers.trigger')}</TableCell>
                  <TableCell sx={{ ...prepThSx, width: PREP_COL.reasons }}>{t('preparation.table.headers.reasons')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && candidates.length === 0 ? (
                  Array.from({ length: 9 }).map((_, index) => (
                    <PrepCandidatesRowSkeleton key={`prep-sk-${index}`} index={index} />
                  ))
                ) : candidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ color: '#94a3b8', py: 3 }}>
                      {t('preparation.table.empty')}
                    </TableCell>
                  </TableRow>
                ) : candidates.map((c) => (
                  <TableRow
                    key={c.ticker}
                    hover
                    onClick={() => onOpenDetail(c)}
                    sx={{
                      cursor: 'pointer',
                      '&:nth-of-type(odd)': { bgcolor: alpha(signalColor(c.signal), 0.02) },
                      '&:hover': { bgcolor: alpha(signalColor(c.signal), 0.055) },
                    }}
                  >
                    <TableCell sx={{ ...prepTdSx, width: PREP_COL.ticker }}>
                      <Tooltip
                        title={c.company_name ? `${c.ticker} — ${c.company_name}` : c.ticker}
                        placement="top"
                      >
                        <Box sx={{
                          color: '#1d4ed8',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.2,
                          width: '100%',
                          minWidth: 0,
                        }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#1d4ed8', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                            {c.ticker}
                          </Typography>
                          {c.company_name ? (
                            <Typography sx={{
                              fontSize: '0.64rem',
                              fontWeight: 600,
                              color: '#94a3b8',
                              lineHeight: 1.25,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}>
                              {c.company_name}
                            </Typography>
                          ) : null}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ ...prepTdNumSx, width: PREP_COL.score, fontWeight: 800, color: '#0f172a', fontFamily: 'monospace' }}>{fmtNum(c.breakout_score, 1)}</TableCell>
                    <TableCell sx={{ ...prepTdSx, width: PREP_COL.signal }}>
                      <Tooltip title={getSignalHint(c.signal) || ''} placement="top" disableHoverListener={!getSignalHint(c.signal)}>
                        <Typography sx={{
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: signalColor(c.signal),
                          lineHeight: 1.3,
                          letterSpacing: '-0.01em',
                          cursor: getSignalHint(c.signal) ? 'help' : 'default',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}>
                          {getLocalizedBreakoutLabel(c.signal_label, i18n.resolvedLanguage)}
                        </Typography>
                      </Tooltip>
                      {getInvestorGuidanceText(c.investor_guidance, i18n.resolvedLanguage) ? (
                        <Typography sx={{
                          fontSize: '0.62rem',
                          color: '#64748b',
                          lineHeight: 1.3,
                          mt: 0.25,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}>
                          {getInvestorGuidanceText(c.investor_guidance, i18n.resolvedLanguage)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ ...prepTdSx, width: PREP_COL.sector, color: '#475569' }}>
                      <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.3, color: '#475569', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {getSectorLabel(c.sector)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ ...prepTdNumSx, fontFamily: 'monospace', color: '#0f172a' }}>{fmtNum(c.price?.close, 0)}</TableCell>
                    <TableCell align="right" sx={{ ...prepTdNumSx, fontFamily: 'monospace', color: c.price?.dist_from_resistance < 0 ? '#0369a1' : '#dc2626' }}>
                      {fmtPct(c.price?.dist_from_resistance)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...prepTdNumSx, color: '#475569' }}>{c.smart_money?.accumulation_days_10 != null ? t('preparation.table.accumulationValue', { value: c.smart_money.accumulation_days_10 }) : '—'}</TableCell>
                    <TableCell align="right" sx={{ ...prepTdNumSx, color: '#475569' }}>{c.smart_money?.up_value_ratio != null ? fmtMultiplier(c.smart_money.up_value_ratio, 2) : '—'}</TableCell>
                    <TableCell sx={prepTdSx}>
                      {c.trigger_type && c.trigger_type !== 'NONE' ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                          <Box sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: triggerColor(c.trigger_type),
                            flexShrink: 0,
                            opacity: 0.9,
                          }} />
                          <Typography sx={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: triggerColor(c.trigger_type),
                            lineHeight: 1.3,
                            letterSpacing: '-0.01em',
                          }}>
                            {getTriggerLabel(c.trigger_type, c.trigger_type_label)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{
                          fontSize: '0.78rem',
                          color: '#94a3b8',
                          fontWeight: 500,
                          lineHeight: 1.3,
                          fontStyle: 'italic',
                          letterSpacing: '-0.01em',
                        }}>
                          {t('preparation.table.noTrigger')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...prepTdSx, width: PREP_COL.reasons }}>
                      {(c.reasons || []).length ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, maxWidth: '100%' }}>
                          {(c.reasons || []).slice(0, 2).map((reason, reasonIndex) => (
                            <Box key={`${c.ticker}-reason-${reasonIndex}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
                              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: reasonIndex === 0 ? '#2563eb' : '#cbd5e1', mt: 0.55, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: reasonIndex === 0 ? '0.67rem' : '0.63rem', fontWeight: reasonIndex === 0 ? 700 : 600, color: reasonIndex === 0 ? '#334155' : '#64748b', lineHeight: 1.25 }}>
                                {localizeReason(reason)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>{t('preparation.table.noReasons')}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
  );
}
